/** @flow */
import { all, put, takeEvery, call } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import {
  GetServiceAbsenceManagementSettings,
  UpdateServiceAbsenceManagementSettings,
} from "@modules/client/constants/actions";
import actionCreators from "@modules/client/actionCreator";
import settingsCreators from "@modules/settings/actionCreator";
import type { ClientServiceAbsenceManagement } from "@modules/client/reducer/service/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import config from "@config";
import { parseSchema } from "@utils/schema";

type ClientRequest = $Exact<{
  clientId: string,
}>;

type ServiceUpdateRequest = $Exact<{
  ...ClientRequest,
  formData: ClientServiceAbsenceManagement,
}>;

export function* onAbsenceManagementSettings({
  payload,
}: Action<ClientRequest>): Generator<*, void, ClientServiceAbsenceManagement> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const [response] = yield all([
      call(
        http.get,
        `${config.endpointBaseUrl}api/v1/client-admin/client/${payload.clientId}/service/absence-management`,
      ),
    ]);

    const schema = yield parseSchema("AbsenceManagementClientServiceDTO");

    yield all([
      put(actionCreators.GetClientServiceSuccess(response.data)),
      put(actionCreators.SetClientServiceSchema(schema)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetClientServiceError(e.response));
  }
}

export function* onUpdateServiceAbsenceManagementSettings({
  payload,
}: Action<ServiceUpdateRequest>): Generator<*, void, { data: ClientServiceAbsenceManagement }> {
  try {
    if (!payload || !payload.clientId || !payload.formData) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `api/v1/client-admin/client/${payload.clientId}/service/absence-management`,
      {
        ...payload.formData,
      },
    );

    yield all([
      put(sendSuccessMessage("SERVICE_SCOPE_WAS_SUCCESSFULLY_UPDATED")),
      put(actionCreators.GetClientServiceSuccess(response.data)),
      put(settingsCreators.GetSettings(payload)),
      put(actionCreators.ToggleAdditionalOptionsModal(false)),
      put(actionCreators.ToggleSelfServiceModal(false)),
      put(actionCreators.ToggleMainOptionsModal(false)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetClientServiceError(e.response));
  }
}

// ROOT
export function* ClientServiceSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetServiceAbsenceManagementSettings, onAbsenceManagementSettings),
    takeEvery(UpdateServiceAbsenceManagementSettings, onUpdateServiceAbsenceManagementSettings),
  ]);
}

export default ClientServiceSagas;
